import { Box, Flex, Heading, Pressable, VStack } from 'native-base';
import React, { memo } from 'react';
import { ImageBackground, Platform, useWindowDimensions } from 'react-native';

import { ProductLogoSpot } from 'app/components/icons';
import RemoteSVG from 'app/components/RemoteSVG';
import { GetDestinationsQuery } from 'app/generated/hygraph';
import productThumbs from 'app/screens/DestinationsScreen/productThumbs';
import { tID } from 'app/services/TestHelper';
import { cdn } from 'app/utils/image';

const LOGO_SIZE = 40;
const MAX_THUMB_SIZE = 400;
const label = ({
  cityName,
  name,
  type,
}: Pick<DestinationCardProps['product'], 'name' | 'type' | 'cityName'>) => name;

export type DestinationCardProps = {
  product: NonNullable<GetDestinationsQuery['products'][0]>;
  onPress: (key: string) => void;
  muted?: boolean;
  preferLocalSpotLogo?: boolean;
  rounded?: string;
  spotUriOverride?: string;
  spotSize?: number;
  spotColor?: string;
  showLabel?: boolean;
  testID?: string;
};

export default memo(function DestinationCard({
  product,
  muted = false,
  onPress,
  preferLocalSpotLogo = false,
  rounded = 'lg',
  spotUriOverride,
  spotSize,
  spotColor = 'black',
  showLabel = true,
  testID,
}: DestinationCardProps) {
  const { width } = useWindowDimensions();
  const { key, thumb, logoSpot, name, cityName, type } = product;
  const spotUri = spotUriOverride ?? (key === 'chicago-c3' ? '' : logoSpot?.url ?? '');

  if (!key) return null;

  return (
    <Pressable
      bgColor="gray.300"
      flex={1}
      overflow="hidden"
      rounded={rounded}
      testID={testID ?? tID(`destination-${product.key}`)}
      onPress={() => onPress(key)}
    >
      <ImageBackground
        defaultSource={productThumbs[key] ?? undefined}
        source={{
          uri: `${cdn(thumb?.url ?? '', [
            ['resize', { width: Math.max((Math.floor(width), MAX_THUMB_SIZE)) }],
            ['quality', { value: 80 }],
          ])}`,
        }}
        style={{ width: '100%', height: '100%' }}
      >
        <Box bgColor="black:alpha.40" bottom="0" h="50%" position="absolute" w="full" />

        <Box rounded={rounded} style={{ aspectRatio: 1 }} testID={tID('DestAspectRatio')} w="full">
          <VStack h="full" justifyContent="center">
            <Flex alignItems="center" h={`${spotSize ?? LOGO_SIZE}px`} position="absolute" w="full">
              {preferLocalSpotLogo ? (
                <ProductLogoSpot color={spotColor} id={key} size={spotSize ?? LOGO_SIZE} />
              ) : (
                <RemoteSVG
                  color={spotColor}
                  fallback={
                    <ProductLogoSpot color={spotColor} id={key} size={spotSize ?? LOGO_SIZE} />
                  }
                  style={{ flex: 1 }}
                  uri={spotUri}
                />
              )}
            </Flex>

            {showLabel && (
              <Box
                justifyContent="center"
                mt="24"
                px="1.5"
                py="1"
                style={Platform.OS !== 'web' ? { minHeight: 66 } : undefined}
              >
                <Heading
                  color="white"
                  fontSize="md"
                  lineHeight="xs"
                  numberOfLines={Platform.OS !== 'web' ? 3 : undefined}
                  style={{
                    textShadowRadius: 5,
                    textShadowColor: '#000',
                    ...(Platform.OS === 'android' && {
                      includeFontPadding: false,
                      letterSpacing: -0.5,
                    }),
                  }}
                  textAlign="center"
                >
                  {label({ cityName, name, type })}
                </Heading>
              </Box>
            )}
          </VStack>
        </Box>

        {muted && (
          <Box bgColor="white:alpha.30" bottom="0" left="0" position="absolute" right="0" top="0" />
        )}
      </ImageBackground>
    </Pressable>
  );
});
